import async from 'async';
import serialize from 'form-serialize';
import { isEmpty, cloneDeep } from 'lodash-es';
import React, { PureComponent } from 'react';
import {
  Alert,
  Button,
  Col,
  Dropdown,
  DropdownButton,
  Form,
  FormControl,
  InputGroup,
  Modal,
  Spinner,
} from 'react-bootstrap';
import { EpisodeRunners } from '../../../../dorian-shared/types/story/Story';
import { PurchaseCurrencyOption } from '../../../../dorian-shared/types/story/Story';
import { api } from '../../../api';
import { RenPyOrientationSelector } from '../../../common/RenPyOrientationSelector';
import { PremiumIpDisabledApprovedEdit } from '../../../utils/premiumIpDisabledApprovedEdit';
import { PremiumIpDisabledEdit } from '../../../utils/premiumIpDisabledEdit';
import { canViewAllContent, isAdminRole, isForeignContentReadOnly } from '../../../utils/userActionUtils';
import { getAddStoryUserAccess } from '../../Stories/AddStoriesUtils';
import { recBonusContent, recChoices, recDecisionPoints } from '../constants';
import { getEpisodeRunnerName, getEpisodeRunnerNames } from '../utils';
import { BonusFormGroup } from './BonusFormGroup/RequiredAchievementFormGroup';
import { CurrencyOverrideFormGroup } from './CurrencyOverrideFormGroup/CurrencyOverrideFormGroup';
import { LockedFormGroup } from './LockedFormGroup/LockedFormGroup';
import { DisableStreamingFormGroup } from './DisableStreamingFormGroup/DisableStreamingFormGroup';
import { OptionAdminRequirements } from './OptionAdminRequirements';
import { OptionDisplaySetings } from './OptionDisplaySetings';

export class OptionPanel extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      validated: false,
      formError: null,
      title: '',
      uuid: '',
      summary: '',
      chapter: null,
      revision: 0,
      types: [],
      book: [],
      recommendations: {
        decisionPoints: recDecisionPoints,
        choices: recChoices,
        bonus_content: recBonusContent,
        stats: [],
      },
      loading: true,
      displaySettings: '',
      displaySettingsError: null,
      public: false,
      episodeErrorLimit: null,
      episodeSummaryErrorLimit: null,
      // eslint-disable-next-line react/no-unused-state
      author: '',
      // eslint-disable-next-line react/no-unused-state
      storyTags: [],
      // eslint-disable-next-line react/no-unused-state
      tags: null,
      isBonus: false,
      isLocked: false,
      purchaseListings: [],
      episodeRunner: null,
      renpyData: null,
      streamDisabled: false,
    };
  }

  handleSubmit = (event) => {
    const { 
      summary,
      isBonus,
      purchaseListings,
      streamDisabled,
      episodeRunner,
      renpyData,
      story_role: storyRole,
    } = this.state;
    const { limits, user, modeEdit, story } = this.props;

    event.preventDefault();
    if (isForeignContentReadOnly(user, storyRole)
      || isForeignContentReadOnly(user, story?.story_role)
      || !modeEdit) {
      return;
    }
    const formData = event.currentTarget;
    let validated = formData.checkValidity();

    if (summary.match(/^\s*$/)) {
      validated = false;
      this.setState({
        validated: false,
        summary: '',
      });
    }

    const storyToSave = serialize(formData, { hash: true });

    storyToSave.public = !!(storyToSave.public || storyToSave.public !== undefined);

    storyToSave.isBonus = isBonus;
    storyToSave.chapter = isBonus ? null : parseInt(storyToSave.chapter, 10);

    const isFirstEpisode = parseInt(storyToSave.chapter, 10) === 1;
    storyToSave.isLocked = isFirstEpisode && !isAdminRole() ? false : (storyToSave.isLocked === 'on');

    storyToSave.purchaseListings = purchaseListings.filter((listing) => listing);
    storyToSave.runner = episodeRunner;
    storyToSave.renpyData = renpyData;

    storyToSave.streamDisabled = streamDisabled;

    if (storyToSave.title && storyToSave.title.length > Number(limits.story_title_max.value)) {
      validated = false;
      this.setState({ validated: false, episodeErrorLimit: true });
      event.stopPropagation();
    }

    if (storyToSave.summary && storyToSave.summary.length > Number(limits.story_summary_max.value)) {
      validated = false;
      this.setState({ validated: false, episodeSummaryErrorLimit: true });
      event.stopPropagation();
    }

    try {
      if (storyToSave.displaySettings) {
        storyToSave.displaySettings = JSON.parse(storyToSave.displaySettings);
      }
    } catch (e) {
      validated = false;
      this.setState({
        validated: false,
        displaySettingsError: e.message,
      });
    }

    if (validated === false) {
      event.stopPropagation();
    } else {
      this.addStories(storyToSave, validated);
    }
    this.setState({ validated: true });
    event.stopPropagation();
  };

  errorAlert = (error) => {
    this.setState({
      formError: error,
    });
    setTimeout(() => {
      this.setState({
        formError: null,
      });
    }, 5000);
  };

  addStories(value, validated) {
    const { onHide, update, id } = this.props;
    if (validated === true) {
      this.setState({
        loading: true,
      });

      api.put(`/v1/stories/${id}`, value)
        .then((res) => {
          this.setState({
            validated: false,
            formError: null,
            pageTitle: res.data.story.title,
            title: res.data.story.title,
            // eslint-disable-next-line react/no-unused-state
            author: res.data.story.author,
            revision: res.data.story.revision,
            summary: res.data.story.summary,
            story_role: res.data.story.story_role,
            chapter: Number(res.data.story.chapter),
            public: res.data.story.public,
            loading: false,
            episodeRunner: res.data.story.runner ?? EpisodeRunners.Dorian,
            renpyData: res.data.story.renpyData,
          }, () => {
            onHide();
          });
          if (update) {
            update();
          }
        })
        .catch((error) => {
          this.setState({
            loading: false,
          });
          if (error.response) {
            this.errorAlert(error.response.data.error);
          }
        });
    }
  }

  componentDidUpdate(prevProps) {
    const { show } = this.props;

    if (prevProps.show !== show) {
      this.loadData();
    }
  }

  loadData() {
    const { id } = this.props;

    this.setState({
      loading: true,
    });
    async.parallel({
      stories: (callback) => {
        api.get(`/v1/stories/${id}`)
          .then((res) => {
            callback(null, res.data.story);
          }).catch((error) => {
            callback(error, null);
          });
      },
      types: (callback) => {
        api.get('/v1/stats/types')
          .then((res) => {
            callback(null, res.data.types);
          }).catch((error) => {
            callback(error, null);
          });
      },
      tags: (callback) => {
        api.get('/v1/tags')
          .then((res) => {
            callback(null, res.data.tags);
          }).catch((error) => {
            callback(error, null);
          });
      },
    }, (err, res) => {
      try {
        if (err) {
          this.setState({
            loading: false,
          });
          if (err.response) {
            this.errorAlert(err.response.data.error);
          }
        } else {
          let displaySettings;
          if (res.stories.displaySettings) {
            displaySettings = JSON.stringify(res.stories.displaySettings, null, 2);
          }
          this.setState({
            types: res.types,
            book: res.stories.book,
            pageTitle: res.stories.title,
            uuid: res.stories.uuid,
            title: res.stories.title,
            displaySettings,
            revision: res.stories.revision,
            summary: res.stories.summary,
            story_role: res.stories.story_role,
            chapter: res.stories.chapter,
            public: res.stories.public,
            recommendations: res.stories.recommendations ? res.stories.recommendations : {
              decisionPoints: recDecisionPoints,
              choices: recChoices,
              bonus_content: recBonusContent,
              stats: [],
            },
            validated: false,
            formError: null,
            loading: false,
            displaySettingsError: null,
            episodeSummaryErrorLimit: null,
            // eslint-disable-next-line react/no-unused-state
            author: res.stories.author,
            // eslint-disable-next-line react/no-unused-state
            storyTags: res.tags,
            // eslint-disable-next-line react/no-unused-state
            tags: res.stories.tags,
            isBonus: res.stories.isBonus,
            isLocked: res.stories.isLocked,
            purchaseListings: cloneDeep(res.stories.purchaseListings ?? []),
            episodeRunner: res.stories.runner ?? EpisodeRunners.Dorian,
            renpyData: res.stories.renpyData ?? null,
            streamDisabled: res.stories.streamDisabled ?? false,
          });
        }
      } catch (e) {
        this.setState({
          loading: false,
        });
        if (e.response) {
          this.errorAlert(e.response.data.error);
        }
      }
    });
  }

  render() {
    const {
      update,
      type,
      show,
      user,
      onHide,
      limits,
      modeEdit,
      story,
      ...other
    } = this.props;

    const {
      validated,
      recommendations,
      episodeErrorLimit,
      displaySettings,
      chapter,
      formError,
      loading,
      pageTitle,
      summary,
      revision,
      uuid,
      types,
      displaySettingsError,
      story_role: storyRole,
      episodeSummaryErrorLimit,
      title,
      show: showFromState,
      public: isPublic,
      book,
      isBonus,
      isLocked,
      purchaseListings,
      episodeRunner,
      renpyData,
      streamDisabled,
    } = this.state;

    const storyTitleMax = Number(limits.story_title_max.value);
    const storySummaryMax = Number(limits.story_summary_max.value);

    const {
      isExistingEpisodeEdit,
      isUserHasMemoryBankAccess,
      isBonusFieldTitle,
    } = getAddStoryUserAccess(user?.role, true);
    const showAdminFields = canViewAllContent(user);
    const adminOnlyDisabled = !isAdminRole(user);
    const isFormDisabled = isForeignContentReadOnly(user, storyRole)
      || isForeignContentReadOnly(user, story?.story_role);

    const isFirstChapter = chapter === 1;
    const isBonusAndLockedVisible = !isFirstChapter || showAdminFields;

    if (show === true) {
      return (
        <Modal
          show={show}
          user={user}
          onHide={onHide}
          limits={limits}
          {...other}
          size="xl"
          aria-labelledby="contained-modal-title-vcenter"
        >
          <Form
            noValidate
            validated={validated}
            onSubmit={(e) => this.handleSubmit(e)}
          >

            <Modal.Header closeButton>
              <Modal.Title>{loading === false ? `Edit episode: "${pageTitle}"` : null}</Modal.Title>
            </Modal.Header>

            <Modal.Body>
              <fieldset
                disabled={isFormDisabled}
                style={{
                  border: 0, margin: 0, padding: 0, minInlineSize: '100%',
                }}
              >

              <Spinner
                animation="border"
                variant="primary"
                className={loading !== false ? 'loadingSpinner justify-content-center' : 'd-none '}
              />

              <Col md={12} className={formError === null ? 'd-none' : 'd-block'}>
                <Alert variant="danger">
                  {formError}
                </Alert>
              </Col>

              <Form.Row>
                <Form.Group as={Col} controlId="StoriesTitle">
                  <Form.Label>Episode Title</Form.Label>
                  <Form.Control
                    required
                    type="text"
                    placeholder="Episode Title"
                    pattern="^([A-Za-z]|[0-9]|_|-| |[.!?,:;()&|[\]\/@]|'|\u0022)+$"
                    name="title"
                    defaultValue={title}
                    className={episodeErrorLimit ? 'text-limit' : null}
                    onChange={(e) => {
                      const { value } = e.target;

                      this.setState({
                        episodeErrorLimit: value && value.length > storyTitleMax,
                        title: value,
                      });
                    }}
                  />
                  <Form.Text className="char-limit-info-box">
                    {storyTitleMax - (title ? title.length : 0)}
                  </Form.Text>

                  <Form.Control.Feedback
                    type="invalid"
                    className={episodeErrorLimit ? 'd-block' : 'd-none'}
                  >
                    Episode Title is too long.
                  </Form.Control.Feedback>

                  <Form.Control.Feedback type="invalid">
                    Please choose a title.
                  </Form.Control.Feedback>
                </Form.Group>
              </Form.Row>

              <Form.Row>

                <Form.Group as={Col} controlId="StorySummary">
                  <Form.Label>Episode Description</Form.Label>
                  <Form.Control
                    required
                    as="textarea"
                    rows="1"
                    maxLength="280"
                    placeholder="Episode Description"
                    name="summary"
                    isInvalid={validated && summary.match(/^\s*$/)}
                    value={summary}
                    className={episodeSummaryErrorLimit ? 'text-limit' : null}
                    onChange={(e) => {
                      const { value } = e.target;
                      this.setState({
                        episodeSummaryErrorLimit: value && value.length > storySummaryMax,
                        summary: value,
                      });
                    }}
                  />
                  <Form.Text className="char-limit-info-box">
                    {storySummaryMax - (summary ? summary.length : 0)}
                  </Form.Text>
                  <Form.Control.Feedback
                    type="invalid"
                    className={episodeSummaryErrorLimit ? 'd-block' : 'd-none'}
                  >
                    Episode Description is too long.
                  </Form.Control.Feedback>
                  <Form.Control.Feedback type="invalid">
                    Please choose a description.
                  </Form.Control.Feedback>
                </Form.Group>
              </Form.Row>

              {showAdminFields && (
              <Form.Row>
                <Col sm="auto">
                  <DisableStreamingFormGroup
                    value={Boolean(streamDisabled)}
                    onChange={(value) => {
                      this.setState({
                        streamDisabled: value,
                      });
                    }}
                    disabled={!isExistingEpisodeEdit || adminOnlyDisabled}
                  />
                </Col>
              </Form.Row>
              )}

              {isUserHasMemoryBankAccess
                  && isBonusAndLockedVisible
                  && (
                    <>
                      {!isFirstChapter && (
                        <Form.Row>
                          <Col sm="auto">
                            <BonusFormGroup
                              value={Boolean(isBonus)}
                              onChange={(value) => {
                                this.setState({
                                  isBonus: value,
                                  isLocked: value,
                                });
                              }}
                              title={isBonusFieldTitle}
                              disabled={!isExistingEpisodeEdit}
                            />
                          </Col>
                        </Form.Row>
                      )}
                      <Form.Row>
                        <Col sm="auto" className="mb-3">
                          <LockedFormGroup
                            value={Boolean(isLocked)}
                            onChange={(value) => {
                              this.setState({
                                isBonus: value ? false : isBonus,
                                isLocked: value,
                              });
                            }}
                            disabled={!isExistingEpisodeEdit || isBonus}
                          />
                        </Col>
                        {isLocked && !isBonus && (
                          <Col md="auto" className="d-flex">
                            <CurrencyOverrideFormGroup
                              value={
                                isEmpty(purchaseListings[0]) && isFirstChapter && showAdminFields
                                  ? { currency: PurchaseCurrencyOption.HeartsOnly, costInHearts: 25 }
                                  : purchaseListings[0]
                              }
                              onChange={(value) => {
                                const newPurchaseListings = [...purchaseListings];
                                newPurchaseListings[0] = value;
                                this.setState({
                                  purchaseListings: newPurchaseListings,
                                });
                              }}
                              disabled={isFirstChapter}
                              label={isFirstChapter ? 'Price' : 'Allow paid override'}
                            />
                          </Col>
                        )}
                      </Form.Row>
                    </>
                  )}

              <Form.Row>
                <Form.Group as={Col} md={4} controlId="StoryChapter">
                  <Form.Label>Episode #</Form.Label>

                  {isBonus
                    ? (
                      <div>
                        This is a bonus episode.
                      </div>
                    )
                    : (
                      <InputGroup>
                        <FormControl
                          required
                          type="text"
                          placeholder="Episode #"
                          name="chapter"
                          pattern="^[0-9]+$"
                          min={1}
                          value={chapter || ''}
                          onChange={(e) => {
                            this.setState({
                              chapter: e.target.value.replace(/[^0-9]/g, ''),
                            });
                          }}
                        />
                        <InputGroup.Append>
                          <Button
                            className="numInpBtn"
                            variant="outline-secondary"
                            onClick={() => {
                              this.setState({
                                chapter: chapter <= 1 ? 1 : Number(chapter) - 1,
                              });
                            }}
                          >
                            &#9660;
                          </Button>
                          <Button
                            className="numInpBtn"
                            variant="outline-secondary"
                            onClick={() => {
                              this.setState({
                                chapter: Number(chapter) + 1,
                              });
                            }}
                          >
                            &#9650;
                          </Button>
                        </InputGroup.Append>
                        <Form.Control.Feedback type="invalid">
                          Please choose an episode number.
                        </Form.Control.Feedback>
                      </InputGroup>
                    )}
                </Form.Group>

                <Form.Group as={Col} md={1} controlId="StoryVersion">
                  <Form.Label>Version</Form.Label>

                  <InputGroup>
                    <FormControl
                      type="text"
                      placeholder="Version"
                      name="version"
                      min={1}
                      disabled
                      value={revision || 1}
                    />
                  </InputGroup>
                </Form.Group>

              </Form.Row>

              {showAdminFields && (
              <Form.Row>
                <Form.Group as={Col} controlId="StoryUUID">
                  <Form.Label>Episode UUID</Form.Label>
                  <FormControl
                    type="text"
                    placeholder="Episode UUID"
                    disabled
                    value={uuid || ''}
                  />
                </Form.Group>
              </Form.Row>
              )}

              {showAdminFields && book.runner !== EpisodeRunners.RenPy && (
                <Alert variant="light" className="m-0 p-0">
                  <Form.Row className="align-items-center">
                    <Form.Label as={Col} sm={2} controlId="bookRunner">Episode Runner: </Form.Label>
                    <Form.Group as={Col} sm={2} controlId="bookRunner" className="m-0 py-1">
                      <DropdownButton
                        size="sm"
                        as={InputGroup.Append}
                        variant="outline-secondary"
                        title={episodeRunner ? getEpisodeRunnerName(episodeRunner) : getEpisodeRunnerName(EpisodeRunners.Dorian)}
                        id="bookRunner"
                        className="full-width-dropdown"
                        disabled={adminOnlyDisabled}
                      >
                        {
                          getEpisodeRunnerNames().map((item) => (
                            <Dropdown.Item
                              key={item.id}
                              eventKey={item.id}
                              active={(item.id === episodeRunner || (!episodeRunner && item.id === EpisodeRunners.Dorian))}
                              onClick={() => {
                                this.setState({
                                  episodeRunner: item.id,
                                });
                              }}
                            >
                              {item.name}
                            </Dropdown.Item>
                          ))
                        }
                      </DropdownButton>
                    </Form.Group>
                    {episodeRunner === EpisodeRunners.RenPy && (
                      <RenPyOrientationSelector
                        value={renpyData?.orientation ?? ''}
                        onChange={(orientation) => {
                          const newRenpyData = { ...renpyData, orientation };
                          this.setState({ renpyData: newRenpyData });
                        }}
                        isDisabled={adminOnlyDisabled}
                        validated={validated}
                        required
                        controlId="orientationSelect"
                      />
                    )}
                  </Form.Row>
                </Alert>
              )}

              <OptionAdminRequirements
                user={user}
                recommendations={recommendations}
                types={types}
                show={showFromState}
                public={isPublic}
                disabled={adminOnlyDisabled}
              />

              {showAdminFields && (
              <OptionDisplaySetings
                defaultValue={displaySettings}
                error={displaySettingsError}
                disabled={adminOnlyDisabled}
              />
              )}

              </fieldset>
            </Modal.Body>

            <Modal.Footer>
              <Button
                type="reset"
                variant="secondary"
                onClick={() => {
                  onHide();
                }}
              >
                Cancel
              </Button>

              <Button
                disabled={(
                    (
                      user.role !== 'admin'
                      && storyRole !== 'owner'
                      && storyRole !== 'editor'
                      && storyRole !== 'co-author'
                    )
                    || loading
                    || !modeEdit
                    || isFormDisabled
                    || (title && title.length > storyTitleMax)
                    || (summary && summary.length > storySummaryMax)
                    || PremiumIpDisabledEdit(user.role, book)
                    || PremiumIpDisabledApprovedEdit(user.role, book)
                  )}
                type="submit"
                variant="primary"
              >
                Save
              </Button>

            </Modal.Footer>
          </Form>
        </Modal>
      );
    }

    return null;
  }
}
